import { http } from "./client";

// Administration: role permissions, data import/export, employee-import data
// mapping and the employee ID format. Every function returns the response body.
const data = (r) => r.data;
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && v !== false,
    ),
  );

/** Filename from a Content-Disposition header, if any. */
const fileNameFrom = (headers, fallback) => {
  const header = headers?.["content-disposition"] || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : fallback;
};
/** Blob download → { blob, fileName, rowCount } */
const asFile = (fallback) => (r) => ({
  blob: r.data,
  fileName: fileNameFrom(r.headers, fallback),
  rowCount:
    r.headers?.["x-row-count"] !== undefined
      ? Number(r.headers["x-row-count"])
      : null,
});

/* ---------- Role permissions ---------- */
export const permissionsApi = {
  // { modules: [{ key, label, description }], scopes: ['COMPANY','BRANCH'], employeeTypes: [null,'Internal','Deployed'] }
  getModules: () => http.get("/permissions/modules").then(data),
  // { superAdmin, role, configured, modules: { [key]: { view, modify, scope, employeeType } } }
  getMine: () => http.get("/permissions/me").then(data),
  // [{ id, title, isActive, company, configured, permissions: { [key]: { view, modify, scope } } }]
  getRoles: () => http.get("/permissions/roles").then(data),
  getRole: (roleId) => http.get(`/permissions/roles/${roleId}`).then(data),
  // permissions: [{ module, canView, canModify, scope, employeeType: null|Internal|Deployed }]
  // (left-out modules = no access; employeeType null = all employees)
  saveRole: (roleId, permissions) =>
    http.put(`/permissions/roles/${roleId}`, { permissions }).then(data),
  // Back to the role's old (unconfigured) behavior
  resetRole: (roleId) =>
    http.delete(`/permissions/roles/${roleId}`).then(() => roleId),
};

/* ---------- Import / export ---------- */
export const dataExchangeApi = {
  getDatasets: () => http.get("/data-exchange/datasets").then(data),
  // { dataset, columns: [{ field, header? }], filters, format: xlsx|csv, templateId?, fileName? } → file
  exportFile: (body) =>
    http
      .post("/data-exchange/export", body, { responseType: "blob" })
      .then(asFile(`${body.dataset}.${body.format || "xlsx"}`)),
  importTemplate: ({ dataset, format = "xlsx", templateId }) =>
    http
      .get("/data-exchange/import-template", {
        params: clean({ dataset, format, templateId }),
        responseType: "blob",
      })
      .then(asFile(`${dataset}_template.${format}`)),
  /**
   * POST /data-exchange/import (multipart: file, dataset, mode, dryRun, templateId?, mapping? JSON)
   * → { mapping, ignoredHeaders, warnings, summary: { total, create, update, skip, failed }, rows, rowsTruncated, logId? }
   */
  importFile: ({ file, dataset, mode, dryRun, templateId, mapping }) => {
    const form = new FormData();
    form.append("file", file);
    form.append("dataset", dataset);
    form.append("mode", mode);
    form.append("dryRun", dryRun ? "true" : "false");
    if (templateId) form.append("templateId", templateId);
    if (mapping) form.append("mapping", JSON.stringify(mapping));
    return http.post("/data-exchange/import", form).then(data);
  },
  importErrors: (logId, format = "xlsx") =>
    http
      .get(`/data-exchange/imports/${logId}/errors`, {
        params: { format },
        responseType: "blob",
      })
      .then(asFile(`import_${logId}_errors.${format}`)),
  getImports: (params = {}) =>
    http.get("/data-exchange/imports", { params: clean(params) }).then(data),

  // Saved export / import templates
  getTemplates: (params = {}) =>
    http.get("/data-exchange/templates", { params: clean(params) }).then(data),
  createTemplate: (body) =>
    http.post("/data-exchange/templates", body).then(data),
  updateTemplate: (id, body) =>
    http.patch(`/data-exchange/templates/${id}`, body).then(data),
  deleteTemplate: (id) =>
    http.delete(`/data-exchange/templates/${id}`).then(() => id),
};

/* ---------- Data mapping (full employee import) ---------- */
export const dataMappingApi = {
  // [{ module: 'employee', label, default: { template, modules: [...] } }]
  getModules: () => http.get("/data-mapping/modules").then(data),
  getList: (module = "employee") =>
    http.get("/data-mapping", { params: { module } }).then(data),
  // The mapping imports will actually use (company → shared → built-in)
  getEffective: (companyId) =>
    http
      .get("/data-mapping/employee/default", { params: clean({ companyId }) })
      .then(data),
  create: (body) => http.post("/data-mapping", body).then(data),
  update: (id, body) => http.patch(`/data-mapping/${id}`, body).then(data),
  // Deactivates → imports fall back to the default
  remove: (id) => http.delete(`/data-mapping/${id}`).then(() => id),
  templateFile: (companyId) =>
    http
      .get("/data-mapping/employee/template-file", {
        params: clean({ companyId }),
        responseType: "blob",
      })
      .then(asFile("employee_import_template.xlsx")),
};

/* ---------- Employee ID format ---------- */
export const employeeIdConfigApi = {
  // Current config (404 = none yet)
  get: (companyId) => http.get(`/employee-id-config/${companyId}`).then(data),
  // Next ID without saving (nothing is used up) → { preview, source: CLIENT|COMPANY|GLOBAL|BUILT_IN, configId }
  preview: (companyId, { clientId, hiringDate } = {}) =>
    http
      .get(`/employee-id-config/${companyId}/preview`, {
        params: clean({ clientId, hiringDate }),
      })
      .then(data),
  // Per-client formats (deployed employees): [{ clientId, clientName, clientCode, status, config | null, preview | null }]
  getClients: () => http.get("/employee-id-config/clients").then(data),
  getClient: (clientId) =>
    http.get(`/employee-id-config/client/${clientId}`).then(data),
  // Same body as the company format + isActive
  saveClient: (clientId, body) =>
    http.put(`/employee-id-config/client/${clientId}`, body).then(data),
  // Back to the company format
  removeClient: (clientId) =>
    http.delete(`/employee-id-config/client/${clientId}`).then(data),
  // { prefix?, includeYear?, numberLength? (1-12), separator?, suffix?, resetEveryYear?, format?, nextNumber? }
  save: (companyId, body) =>
    http.put(`/employee-id-config/${companyId}`, body).then(data),
};

/** Save a { blob, fileName } to the user's computer. */
export const saveFile = ({ blob, fileName }) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
